/** Session controls — back/forward navigation buttons and submit action with contextual labels. */
import { useCallback, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ENFORCED_READING_MS } from '../hooks/useEnforcedReading';
import { Loader2, ArrowLeft, ArrowRight, PencilLine } from 'lucide-react';

import { useSession } from '../SessionContext';

// ─── Types ───────────────────────────────────────────────────────────────────

interface SessionControlsProps {
  onSubmit?: () => void;
  /** Called when info step Continue is pressed (sends server-side submit). */
  onSubmitInfo?: () => void;
  /** When true, the forward button is disabled (enforced explanation reading). */
  blockNext?: boolean;
}

/** Whether the forward button is a compact icon or wide text button. */
type ForwardShape = 'icon' | 'wide';

interface ForwardConfig {
  label: string;
  shape: ForwardShape;
  disabled: boolean;
  loading: boolean;
  aiGrading: boolean;
  compact: boolean;
  action: (() => void) | null;
}

// ─── Forward button state derivation ─────────────────────────────────────────

function useForwardConfig(onSubmit?: () => void, onSubmitInfo?: () => void): ForwardConfig {
  const { state, dispatch, canSubmit, canGoNext, currentStep, frontier } = useSession();
  const { phase, currentIndex, steps, isReviewing, gradingPhase } = state;
  const isLastStep = currentIndex === steps.length - 1;
  const stepKind = currentStep?.stepKind ?? 'info';

  // Detect AI grading from NDJSON streaming events
  const isAiGrading =
    gradingPhase !== null &&
    'phase' in gradingPhase &&
    (gradingPhase.phase === 'ai_calling' || gradingPhase.phase === 'ai_escalating');

  return useMemo(() => {
    // Reviewing past steps: show "Back to current"
    if (isReviewing) {
      // When all steps are answered (frontier === -1), advance via NEXT_STEP
      // which will either navigate to the next step or trigger completion.
      const action =
        frontier === -1
          ? () => dispatch({ type: 'NEXT_STEP' })
          : () => dispatch({ type: 'GO_TO_STEP', index: frontier });
      return {
        label: frontier === -1 ? 'Finish' : 'Back to current',
        shape: 'wide' as const,
        disabled: false,
        loading: false,
        aiGrading: false,
        compact: false,
        action,
      };
    }

    // Submitting / grading: wide with spinner + label
    if (phase === 'submitting' || phase === 'grading') {
      // Info steps: wide spinner with "Loading" (not "Checking" — nothing is being graded)
      if (stepKind === 'info') {
        return {
          label: 'Loading',
          shape: 'wide' as const,
          disabled: true,
          loading: true,
          aiGrading: false,
          compact: false,
          action: null,
        };
      }
      const label = isAiGrading ? 'Checking with AI' : 'Checking';
      return {
        label,
        shape: 'wide' as const,
        disabled: true,
        loading: true,
        aiGrading: isAiGrading,
        compact: false,
        action: null,
      };
    }

    // Graded: Next or Finish
    if (phase === 'graded') {
      const goForward = () => dispatch({ type: 'NEXT_STEP' });
      const label = isLastStep ? 'Finish' : 'Next';
      return {
        label,
        shape: 'wide' as const,
        disabled: false,
        loading: false,
        aiGrading: false,
        compact: false,
        action: goForward,
      };
    }

    // Info step: Continue (submits to server, then advances)
    if (stepKind === 'info' && phase === 'idle') {
      return {
        label: isLastStep ? 'Finish' : 'Continue',
        shape: 'wide' as const,
        disabled: false,
        loading: false,
        aiGrading: false,
        compact: false,
        action: onSubmitInfo ?? (() => dispatch({ type: 'NEXT_STEP' })),
      };
    }

    // Question step: wide Check button
    if (stepKind === 'question') {
      if (canSubmit) {
        return {
          label: 'Check',
          shape: 'wide' as const,
          disabled: false,
          loading: false,
          aiGrading: false,
          compact: false,
          action: onSubmit ?? (() => dispatch({ type: 'SUBMIT' })),
        };
      }
      return {
        label: 'Check',
        shape: 'wide' as const,
        disabled: true,
        loading: false,
        aiGrading: false,
        compact: false,
        action: null,
      };
    }

    // Fallback
    if (canGoNext) {
      const goForward = () => dispatch({ type: 'NEXT_STEP' });
      return {
        label: 'Continue',
        shape: 'wide' as const,
        disabled: false,
        loading: false,
        aiGrading: false,
        compact: false,
        action: goForward,
      };
    }

    return {
      label: 'Continue',
      shape: 'wide' as const,
      disabled: true,
      loading: false,
      aiGrading: false,
      compact: false,
      action: null,
    };
  }, [
    phase,
    stepKind,
    isLastStep,
    isReviewing,
    isAiGrading,
    frontier,
    canSubmit,
    canGoNext,
    dispatch,
    onSubmit,
    onSubmitInfo,
  ]);
}

// ─── Forward button (morphs between icon and wide) ──────────────────────────

function ForwardButton({ config }: { config: ForwardConfig }) {
  const isIcon = config.shape === 'icon';
  const isActive = !config.disabled && !config.loading && !config.compact;

  const handleClick = useCallback(() => {
    config.action?.();
  }, [config]);

  return (
    <motion.button
      type="button"
      disabled={config.disabled}
      onClick={handleClick}
      className="relative flex items-center justify-center font-semibold rounded-full cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 overflow-hidden"
      style={{
        background: 'var(--f-accent)',
        color: 'var(--f-fg-on-accent)',
        boxShadow: config.disabled ? 'none' : 'var(--f-shadow-accent)',
        fontFamily: 'var(--f-font-display)',
        height: 48,
        willChange: 'transform',
      }}
      animate={{
        width: config.compact ? 48 : isIcon ? 48 : config.aiGrading ? 200 : 160,
      }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      whileHover={isActive ? { scale: 1.03 } : undefined}
      whileTap={isActive ? { scale: 0.95 } : undefined}
      aria-label={isIcon ? config.label : undefined}
    >
      <AnimatePresence mode="wait" initial={false}>
        {config.compact ? (
          <motion.span
            key="compact"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 0.6, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.15 }}
            className="flex items-center justify-center"
          >
            <Loader2 size={20} className="animate-spin" style={{ color: 'var(--f-fg-muted)' }} />
          </motion.span>
        ) : config.loading ? (
          <motion.span
            key={config.aiGrading ? 'ai-grading' : 'loading'}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
            className="flex items-center gap-2"
          >
            <Loader2 size={16} className="animate-spin" />
            <span>{config.label}</span>
          </motion.span>
        ) : isIcon ? (
          <motion.span
            key="icon"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.15 }}
            className="flex items-center justify-center"
          >
            <ArrowRight size={22} />
          </motion.span>
        ) : (
          <motion.span
            key={config.label}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
            className="flex items-center gap-2"
          >
            {config.label === 'Check' && <PencilLine size={16} />}
            <span>{config.label}</span>
          </motion.span>
        )}
      </AnimatePresence>
    </motion.button>
  );
}

// ─── Back button (always in DOM, fades via opacity) ─────────────────────────

function BackButton() {
  const { canGoBack, dispatch } = useSession();

  return (
    <button
      type="button"
      disabled={!canGoBack}
      onClick={() => dispatch({ type: 'GO_BACK' })}
      className="flex items-center justify-center rounded-full cursor-pointer disabled:cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
      style={{
        width: 48,
        height: 48,
        background: 'var(--f-bg-sunken)',
        color: 'var(--f-fg)',
        opacity: canGoBack ? 1 : 0,
        pointerEvents: canGoBack ? 'auto' : 'none',
        transition: 'opacity 0.3s ease',
        willChange: 'transform',
      }}
      aria-label="Go back"
    >
      <ArrowLeft size={22} />
    </button>
  );
}

// ─── Forward nav button (mirrors BackButton, visible when browsing) ──────────

function ForwardNavButton() {
  const { state, dispatch, frontier } = useSession();
  // Only show when explicitly browsing past steps AND before the frontier
  const show = state.isReviewing && state.currentIndex < frontier;

  if (!show) return <div style={{ width: 48, height: 48 }} />;

  return (
    <button
      type="button"
      onClick={() => dispatch({ type: 'NEXT_STEP' })}
      className="flex items-center justify-center rounded-full cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
      style={{
        width: 48,
        height: 48,
        background: 'var(--f-bg-sunken)',
        color: 'var(--f-fg)',
        willChange: 'transform',
      }}
      aria-label="Go forward"
    >
      <ArrowRight size={22} />
    </button>
  );
}

// ─── Footer action button ────────────────────────────────────────────────────

export function SessionControls({
  onSubmit,
  onSubmitInfo,
  blockNext,
}: Pick<SessionControlsProps, 'onSubmit' | 'onSubmitInfo' | 'blockNext'>) {
  const config = useForwardConfig(onSubmit, onSubmitInfo);

  // Increment key each time enforced reading activates to re-trigger the fade animation
  const [fadeKey, setFadeKey] = useState(0);
  const [prevBlockNext, setPrevBlockNext] = useState(blockNext);
  if (blockNext && !prevBlockNext) {
    setFadeKey((k) => k + 1);
  }
  if (blockNext !== prevBlockNext) {
    setPrevBlockNext(blockNext);
  }

  return (
    <div className="relative flex w-full items-center justify-center">
      <div className="absolute left-0">
        <BackButton />
      </div>
      <motion.div
        key={fadeKey}
        initial={blockNext ? { opacity: 0.5 } : false}
        animate={{ opacity: 1 }}
        transition={{ duration: ENFORCED_READING_MS / 1000, ease: 'easeOut' }}
        style={{ pointerEvents: blockNext ? 'none' : 'auto' }}
      >
        <ForwardButton config={config} />
      </motion.div>
      <div className="absolute right-0">
        <ForwardNavButton />
      </div>
    </div>
  );
}
